import React, { useState } from 'react';
import { Star, StarHalf, Home, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Review } from '../types/review';
import { trackImageGalleryOpen, trackExternalLinkClick } from '../utils/analytics';

interface ImageGalleryProps {
  images: string[];
  initialIndex: number;
  onClose: () => void;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
      >
        <X className="w-8 h-8" />
      </button>
      
      {images.length > 1 && (
        <>
          <button
            onClick={handlePrevious}
            className="absolute left-4 text-white hover:text-gray-300 z-10"
          >
            <ChevronLeft className="w-12 h-12" />
          </button>
          
          <button
            onClick={handleNext}
            className="absolute right-4 text-white hover:text-gray-300 z-10"
          >
            <ChevronRight className="w-12 h-12" />
          </button>
        </>
      )}

      <button
        onClick={handlePrevious}
        className="absolute left-4 text-black hover:text-gray-600"
      >
        <ChevronLeft className="w-12 h-12" />
      </button>
      
      <button
        onClick={handleNext}
        className="absolute right-4 text-black hover:text-gray-600"
      >
        <ChevronRight className="w-12 h-12" />
      </button>

      <div className="relative max-w-[90vw] max-h-[90vh]">
        <img
          src={images[currentIndex]}
          alt={`Property image ${currentIndex + 1}`}
          className="max-w-full max-h-[90vh] object-contain"
        />
        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
            {currentIndex + 1} / {images.length}
          </div>
        )}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-black text-sm">
          {currentIndex + 1} / {images.length}
        </div>
      </div>
    </div>
  );
};

interface ReviewCardProps {
  review: Review;
  address: string;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ review, address }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    if (review.images) {
      trackImageGalleryOpen(address, review.images.length);
    }
  };

  const handleExternalLinkClick = (url: string, linkText: string) => {
    trackExternalLinkClick(url, linkText);
  };

  // Debug logging for anonymous review issue
  console.log('Review object in ReviewCard:', review);
  console.log('is_anonymous:', review.is_anonymous, 'reviewer_name:', review.reviewer_name);

  const renderCircularStars = (rating: number, size: 'sm' | 'lg' = 'sm') => {
    const circleSize = size === 'lg' ? 'w-6 h-6' : 'w-4 h-4';
    const starSize = size === 'lg' ? 'w-4 h-4' : 'w-3 h-3';
    
    const getStarColor = (starRating: number) => {
      if (starRating >= 4.2) return '#008d49'; // 5 - dark green
      if (starRating >= 3.4) return '#a1e883'; // 4 - light green  
      if (starRating >= 2.6) return '#ffd830'; // 3 - yellow
      if (starRating >= 1.8) return '#f86306'; // 2 - orange
      return '#ef001d'; // 1 - red
    };
    
    return (
      <div className="flex">
        {[0, 1, 2, 3, 4].map((index) => {
          const filled = rating - index;
          const starColor = getStarColor(rating);
          return (
            <div key={index} className={`${circleSize} rounded-full flex items-center justify-center`}
                 style={{ backgroundColor: filled >= 1 ? starColor : (filled > 0 ? starColor : '#d1d5db') }}>
              {filled >= 1 ? (
                <Star className={`${starSize} text-white fill-white`} />
              ) : filled > 0 ? (
                <StarHalf className={`${starSize} text-white fill-white`} />
              ) : (
                <Star className={`${starSize} text-white fill-white opacity-60`} />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        {/* Black Header */}
        <div className="bg-black text-white p-4 flex items-start gap-3 min-w-0 max-w-full">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#32a86c' }}>
            <Home className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0 max-w-full overflow-hidden">
            <h3 className="font-bold text-sm leading-tight break-words overflow-wrap-anywhere">{address}</h3>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6 max-w-full overflow-hidden">
          {/* Posted Date - Mobile: Top, Desktop: Top Right */}
          <div className="flex justify-center md:justify-end mb-2 md:mb-0">
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <img 
                src="https://rentreview.io/wp-content/uploads/2025/08/Date-posted.png" 
                alt="Date posted" 
                className="w-4 h-4"
              />
              <span className="font-bold text-gray-900">Posted: </span>
              <span className="text-gray-600">{new Date(review.created_at).toLocaleDateString()}</span>
            </div>
          </div>

          <div className="mb-4 md:-mt-6">
          
          {/* Reviewer Name */}
          <div className="text-base text-gray-500 mb-2">
            <span className="font-bold text-gray-900">Tenant Name: </span>
            <span className="text-gray-600">{review.is_anonymous ? 'Anonymous' : review.reviewer_name}</span>
          </div>
          
          {/* Time in Property */}
          <div className="text-base text-gray-600 mb-2">
            <span className="font-bold text-gray-900">Time in Property: </span>
            <span className="text-gray-600">{review.time_in_property}</span>
            {review.date_in_property && (
              <span className="ml-2 text-gray-600">
                (From: {review.date_in_property})
              </span>
            )}
          </div>
          
          {/* Property Ownership Information */}
          <div className="text-base text-gray-600 mb-2">
            <span className="font-bold text-gray-900">Property Ownership: </span>
            <span className="text-gray-600">
              {review.property_owner === 'private-individual' && 'Private Landlord'}
              {review.property_owner === 'private-company' && (
                <>Private Company - {review.private_company_name || 'Unknown Company'}</>
              )}
              {review.property_owner === 'student-accommodation' && (
                <>Student Accommodation - {review.student_accommodation_name || 'Unknown Provider'}</>
              )}
              {review.property_owner === 'housing-association' && (
                <>Housing Association - {review.council_housing_name || 'Unknown Housing Association'}</>
              )}
              {review.property_owner === 'council' && (
                <>Council - {review.council_housing_name || 'Unknown Council'}</>
              )}
              {review.property_owner === 'unknown' && 'Unknown'}
              {!review.property_owner && 'Not specified'}
            </span>
          </div>
          
          {/* Letting Agent Information */}
          {review.letting_agent_name && (
            <div className="text-base text-gray-600 mb-1">
              <span className="font-bold text-gray-900">Letting Agent: </span>
              <span className="text-gray-600">{review.letting_agent_name}</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 border-t border-gray-400 pt-4">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              {renderCircularStars(review.property_rating)}
              <div className="text-base font-bold text-gray-900">Property</div>
            </div>
            {review.property_review && (
              <p className="text-sm text-gray-700 pl-1">{review.property_review}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              {renderCircularStars(review.location_rating)}
              <div className="text-base font-bold text-gray-900">Location</div>
            </div>
            {review.location_review && (
              <p className="text-sm text-gray-700 pl-1">{review.location_review}</p>
            )}
          </div>
          
          {review.letting_agent_name && (
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                {renderCircularStars(review.agent_rating)}
                <div className="text-base font-bold text-gray-900">Letting Agent</div>
              </div>
              {review.agent_review && (
                <p className="text-sm text-gray-700 pl-1">{review.agent_review}</p>
              )}
            </div>
          )}
          
          {review.landlord_rating && (
            <div className="space-y-2">
             {(() => {
               let landlordOwnershipSuffix = '';
               switch (review.property_owner) {
                 case 'private-individual':
                   landlordOwnershipSuffix = ' (Private Landlord)';
                   break;
                 case 'private-company':
                   landlordOwnershipSuffix = ' (Private Company)';
                   break;
                 case 'housing-association':
                   landlordOwnershipSuffix = ' (Housing Association)';
                   break;
                 case 'council':
                   landlordOwnershipSuffix = ' (Council)';
                   break;
                 case 'student-accommodation':
                   landlordOwnershipSuffix = ' (Student Accommodation)';
                   break;
                 case 'unknown':
                   landlordOwnershipSuffix = ' (Unknown)';
                   break;
                 default:
                   landlordOwnershipSuffix = '';
               }
               
               return (
                 <>
              <div className="flex items-center gap-3">
                {renderCircularStars(review.landlord_rating)}
                    <div className="text-base font-bold text-gray-900">Landlord{landlordOwnershipSuffix}</div>
              </div>
              {review.landlord_review && (
                <p className="text-sm text-gray-700 pl-1">{review.landlord_review}</p>
              )}
                 </>
               );
             })()}
            </div>
          )}
        </div>

        {/* Images - only show if ai_images_status is not 'fail' */}
        {review.images && review.images.length > 0 && review.ai_images_status !== 'fail' && (
          <div className="flex flex-wrap gap-2 mb-4 max-w-full overflow-hidden">
            {review.images.map((url, index) => (
              <button
                key={index}
                onClick={() => handleImageClick(index)}
                className="focus:outline-none flex-shrink-0"
              >
                <img 
                  src={url}
                  alt={`Property image ${index + 1}`}
                  className="h-[150px] w-auto max-w-full rounded cursor-pointer hover:opacity-90 transition-opacity"
                />
              </button>
            ))}
          </div>
        )}
        
        {/* Letting Agent Details Section */}
        {(review.letting_agent_website || review.letting_agent_phone || review.time_in_property) && (
          <div className="mt-4 pt-4 border-t border-gray-400 max-w-full overflow-hidden">
            <h4 className="font-bold text-gray-900 text-base mb-2">Letting Agent Details</h4>
            <div className="space-y-1 text-sm break-words">
              {review.letting_agent_name && (
                <div>
                  <div className="font-bold text-gray-900 flex items-center gap-1">
                    <img 
                      src="https://rentreview.io/wp-content/uploads/2025/08/Letting-Agent-1.png" 
                      alt="Letting Agent" 
                      className="w-4 h-4"
                    />
                    Agent Name: 
                    <span className="text-gray-600 font-normal">
                      {review.letting_agent_name}
                    </span>
                  </div>
                </div>
              )}
              {review.letting_agent_address && (
                <div>
                  <div className="font-bold text-gray-900 flex items-center gap-1">
                    <img 
                      src="https://rentreview.io/wp-content/uploads/2025/08/Icon-pin-2.png" 
                      alt="Address" 
                      className="w-4 h-4"
                    />
                    Address: 
                    <span className="text-gray-600 font-normal">
                      {review.letting_agent_address}
                    </span>
                  </div>
                </div>
              )}
              {review.letting_agent_phone && (
                <div>
                  <div className="font-bold text-gray-900 flex items-center gap-1 break-all">
                    <img 
                      src="https://rentreview.io/wp-content/uploads/2025/08/Phone-1.png" 
                      alt="Phone" 
                      className="w-4 h-4"
                    />
                    Phone: 
                  <a 
                    href={`tel:${review.letting_agent_phone}`}
                    className="text-blue-600 hover:text-blue-800 font-normal"
                    onClick={() => handleExternalLinkClick(`tel:${review.letting_agent_phone}`, 'Letting Agent Phone')}
                  >
                    {review.letting_agent_phone}
                  </a>
                  </div>
                </div>
              )}
              {review.letting_agent_website && (
                <div>
                  <div className="font-bold text-gray-900 flex items-center gap-1 mb-1">
                    <img 
                      src="https://rentreview.io/wp-content/uploads/2025/08/Letting-Agent-1.png" 
                      alt="Website" 
                      className="w-4 h-4"
                    />
                    Website:
                  </div>
                  <div className="ml-5 break-all">
                    <a 
                      href={review.letting_agent_website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline font-normal"
                      onClick={() => handleExternalLinkClick(review.letting_agent_website!, 'Letting Agent Website')}
                    >
                      {review.letting_agent_website}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      </div>

      {selectedImageIndex !== null && review.images && (
        <ImageGallery
          images={review.images}
          initialIndex={selectedImageIndex}
          onClose={() => setSelectedImageIndex(null)}
        />
      )}
    </>
  );
};

export default ReviewCard;